<template>
  <div>
    <div class="bg-light pb-5">
      <div class="container section-header-breadcrumb">
        <div class="row">
          <div class="content text-left col-sm-12 col-md-12 col-lg-12">
            <h2 class="title-page">{{ $t("payment.title") }}</h2>
            <label>{{ $t("payment.breadcrumb") }}</label>
          </div>
        </div>
      </div>

      <div class="container">
        <div class="row">
          <div class="col-lg-11">
            <div class="bg-white round-10 p-5">
              <div class="row justify-content-center">
                <div class="col-lg-8">
                  <div
                    class="d-flex flex-column gap-3 align-items-center justify-content-center"
                  >
                    <img :src="checkcard" alt="Check Card" />
                    <p
                      class="font-bold blue-color font-16 text-center mt-0"
                      v-html="$sanitize.sanitizeI18nContent(thankYouMessage)"
                    ></p>
                    <p class="text-center">
                      {{ $t("payment.order_validated") }}
                    </p>

                    <!-- Adresse de livraison -->
                    <div
                      class="adresse-payment-box mt-3 mb-2"
                      v-if="order && order.shippingAddress"
                    >
                      <i class="ti ti-location-pin"></i>
                      <div>
                        <p class="mb-0">
                          {{ shippingAddress.street }},
                          {{ shippingAddress.city }},
                          {{ shippingAddress.postcode }},
                          {{ shippingAddress.countryCode }}
                        </p>
                      </div>
                    </div>

                    <!-- Produits -->
                    <div class="cart-product w-100" v-if="order && order.items">
                      <!-- Display all items normally, no visual distinction for promotions -->
                      <div
                        v-for="item in displayItems"
                        :key="item.uniqueKey || item.id"
                        class="cart-payment-block d-flex align-items-center justify-content-between"
                      >
                        <div
                          class="cart-payment-block-left d-flex gap-3 align-items-center"
                        >
                          <img
                            :src="getProductImage(item)"
                            alt="Product Image"
                          />
                          <div>
                            <h4>{{ item.productName }}</h4>
                            <small>{{ item.variantDetails.name }}</small>
                            <br>
                            <small v-if="item.cart_message && !isClient && !isDelegate" class="cart-message d-block">{{ item.cart_message }}</small>
                           
                            <span
                              v-if="item.variantDetails?.datePrecommandeSupplyvetEsupplyvet && new Date(item.variantDetails.datePrecommandeSupplyvetEsupplyvet) > new Date()"
                              class="preorder-badge"
                            >
                              Précommande : livraison à partir du
                              {{ new Date(item.variantDetails.datePrecommandeSupplyvetEsupplyvet).toLocaleDateString('fr-FR') }}
                            </span>
                            <br>
                            <small
                              >{{ $t("payment.quantity") }}:
                              {{ item.quantity }}</small
                            >
                            <br>
                          </div>
                        </div>
                        
                        <div class="cart-payment-block-right">
                          <p
                            v-if="item.unitPrice && item.discountedUnitPrice && (item.unitPrice !== item.discountedUnitPrice)"
                            class="stroke font-22"
                          >
                            <strong>{{ ((item.originalUnitPrice / 100)*item.quantity).toFixed(2) }}</strong>
                            <strong>{{ $t("payment.price_ht") }}</strong>
                          </p>
                          <p class="blue-color font-22">
                            <!-- Specific display for UG promotional free units -->
                            <strong v-if="item.isUgPromotionFree">0</strong>
                            <strong v-else-if="item.subtotal === 0">Offert</strong>
                            <strong v-else>{{ (item.subtotal / 100).toFixed(2) }}</strong>
                            
                            <!-- Price label logic -->
                            <small v-if="item.isUgPromotionFree">{{ $t("payment.price_ht") }}</small>
                            <small v-else-if="item.subtotal !== 0">{{ $t("payment.price_ht") }}</small>
                          </p>
                        </div>
                      </div>
                    </div>

                    <!-- Totaux -->
                    <div
                      class="w-100 d-flex flex-column justify-content-end align-items-end"
                      v-if="order"
                    >
                      <p class="mb-0">
                        {{ $t("payment.subtotal") }}
                        {{ (order.itemsSubtotal / 100).toFixed(2) }} {{ $t("payment.currency") }}
                      </p>
                      <p class="mb-0">
                        {{ $t("payment.shipping_fees") }}
                        {{ order.shippingTotal === 0 ? 'offerts' : (order.shippingTotal / 100).toFixed(2) + $t("payment.currency") }}
                      </p>

                      <p class="mb-0" v-if="order.tvaTieCode==='0'">
                        {{ $t("payment.tva") }}
                        {{ ((order.taxTotal - (order.shippingTotal - order.shippingTaxTotal) ) / 100).toFixed(2) }} {{ $t("payment.currency") }}
                      </p>
                      <p>
                        <strong v-if="order.tvaTieCode==='0'">
                          {{ $t("payment.total_ttc") }}
                          {{ (order.total / 100).toFixed(2) }} 
                        </strong>
                        <strong v-else>
                          {{ $t("payment.total_ht") }}
                          {{ (order.total / 100).toFixed(2) }} 
                        </strong>
                      </p>
                    </div>

                    <!-- Bouton espace client -->
                    <div>
                      <button class="btn btn-orange" @click="goToClientSpace">
                        {{ $t("payment.client_space") }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { exportOrderToDivalto, getOrderDetails, getOrderDelegateDetails } from "@/api/orders"; 
import checkcard from "@/assets/images/check-card.svg";
import ProductDefault from "@/assets/images/product_default.png";
import { useRegisterStore } from '@/stores/register';

export default {
  name: "PaymentView",
  props: ["orderToken"],
  data() {
    return {
      checkcard,
      order: null, 
      isExporting: false,
      registerStore: null,
    };
  },
  async mounted() {
    this.registerStore = useRegisterStore();
    
    try {
      let response;
      
      // Use different API call based on user role
      if (this.isDelegate) {
        console.log("Fetching delegate order details");
        response = await getOrderDelegateDetails(this.orderToken);
      } else {
        console.log("Fetching regular order details");
        response = await getOrderDetails(this.orderToken);
      }
      
      this.order = response.data;
      await this.exportToDivalto(); // Trigger Divalto export after order fetch
    } catch (e) {
      console.error("Impossible de récupérer la commande :", e);
    }
  },
  computed: {
    shippingAddress() {
      return this.order?.shippingAddress || {};
    },
    isDelegate() {
      return this.registerStore?.user?.role === "delegues";
    },
    isClient() {
      return this.registerStore?.user?.role === "client";
    },
    earliestPreorderDate() {
      if (!this.order?.items) return null;
      let maxDate = null;
      for (const item of this.order.items) {
        const d = item.variantDetails?.datePrecommandeSupplyvetEsupplyvet;
        if (!d) continue;
        const parsed = new Date(d);
        if (isNaN(parsed.getTime())) continue;
        if (!maxDate || parsed > maxDate) maxDate = parsed;
      }
      return maxDate;
    },
    thankYouMessage() {
      if (this.earliestPreorderDate) {
        const d = this.earliestPreorderDate;
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const dateStr = `${day}/${month}/${year}`;
        return this.$t('payment.thank_you_preorder', { date: dateStr });
      }
      return this.$t('payment.thank_you');
    },
    
    // Combined items for display - maintains original appearance
    displayItems() {
      if (!this.order?.items) return [];
      
      // For non-delegates, show all items normally
      if (!this.isDelegate) {
        return this.order.items;
      }
      
      // For delegates, combine regular items with split promotional items
      const displayItems = [];
      
      this.order.items.forEach(item => {
        // Check if item has UG promotions that need splitting
        if (item.promotionInfo?.hasPromotion && item.promotionInfo?.ugPromotions?.length > 0) {
          const ugPromotion = item.promotionInfo.ugPromotions[0];
          
          // Add paid units as separate item
          if (item.promotionInfo.totalPaidUnits > 0) {
            displayItems.push({
              ...item,
              uniqueKey: `${item.id}-paid`,
              quantity: item.promotionInfo.totalPaidUnits,
              subtotal: item.promotionInfo.totalPaidUnits * item.unitPrice,
              isUgPromotionFree: false
            });
          }
          
          // Add free units as separate item
          if (ugPromotion.discountedUnits > 0) {
            displayItems.push({
              ...item,
              uniqueKey: `${item.id}-free`,
              quantity: ugPromotion.discountedUnits,
              subtotal: 0,
              isUgPromotionFree: true // Flag for UG promotional free units
            });
          }
        } else {
          // Regular items without promotions
          displayItems.push({
            ...item,
            isUgPromotionFree: false
          });
        }
      });
      
      return displayItems;
    }
  },
  methods: {
    // Même logique que Cart.vue : image de la variante, sinon image produit, sinon visuel neutre
    getProductImage(item) {
      const variant = item.variantDetails;

      if (variant?.imagesDetails?.length > 0) {
        return variant.imagesDetails[0].path;
      }

      if (variant?.imagesProduct?.length > 0) {
        const variantImages = variant.imagesProduct.filter(
          (img) =>
            img.productVariants &&
            img.productVariants.includes(`/api/v2/shop/product-variants/${variant.code}`)
        );
        return variantImages.length > 0
          ? variantImages[0].path
          : variant.imagesProduct[0].path;
      }

      return ProductDefault;
    },
    async exportToDivalto() {
      if (!this.orderToken || this.isExporting) return; // Prevent multiple exports
      this.isExporting = true;
      try {
        await exportOrderToDivalto(this.orderToken);
        console.log("Order successfully exported to Divalto");
      } catch (e) {
        console.error("Failed to export order to Divalto:", e);
        // Optionally show user feedback (e.g., toast notification)
        this.$notify({
          title: "Export Error",
          text: "Failed to export order to Divalto. Please try again later.",
          type: "error",
        });
      } finally {
        this.isExporting = false;
      }
    },
    goToClientSpace() {
      this.$router.push({ name: "my-account" });
    },
  },
};
</script>

<style scoped>
.bg-light {
  background-color: #f5f6f9;
}

.section-header-breadcrumb {
  text-align: left;
  padding: 0 15px;
}

.title-page {
  font-size: 30px;
  font-weight: bold;
  color: #1e347d;
}

label {
  font-size: 15px;
  color: #1e347d;
}

.round-10 {
  border-radius: 10px;
}

.font-bold {
  font-weight: bold;
}

.blue-color {
  color: #1e347d;
}

.font-16 {
  font-size: 16px;
}

.font-22 {
  font-size: 22px;
}

.stroke {
  text-decoration: line-through;
}

.adresse-payment-box {
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 2px solid #aeb1ba;
  padding: 16px;
  border-radius: 12px;
}

.cart-payment-block {
  border-bottom: 1px solid #ddd;
  padding: 1rem 0;
}

.cart-payment-block-left img {
  width: 100px;
  height: auto;
}

.btn-orange {
  background-color: #ec621a;
  color: white;
  border-radius: 76px;
  font-size: 18px;
  font-weight: bold;
  padding: 10px 20px;
}

.preorder-badge {
  display: inline-block;
  background-color: #1e347d;
  color: #fff;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 4px;
  margin-top: 2px;
}

.cart-message {
  color: #ec621a;
  font-style: italic;
}
</style>